import { Controller } from "@hotwired/stimulus"

// Passing a public post on. Where the device has a share sheet (phones, the
// apps) it opens; elsewhere the link is copied and the button says so for a
// moment. Only public posts carry the button: what was written for a circle
// stays in it.
export default class extends Controller {
  static targets = ["label"]
  static values = {
    url: String, title: String, text: String,
    // Where to note that a post went on with a place in it, and which post (posts/_with_place).
    note: String, post: String,
    // What the button says after a copy, set by the page in the reader's language.
    copied: { type: String, default: "Link copied" },
    failed: { type: String, default: "Couldn't copy the link" }
  }

  async share(event) {
    event.preventDefault()
    const url = new URL(this.urlValue, location.href).toString()

    if (navigator.share) {
      try {
        await navigator.share({ title: this.titleValue, text: this.textValue, url })
        return
      } catch (error) {
        if (error.name === "AbortError") return
      }
    }

    this.say((await this.copy(url)) ? this.copiedValue : this.failedValue)
  }

  // The words themselves onto the clipboard, with no share sheet in the way:
  // an invitation is usually pasted into a conversation already open.
  async copyText(event) {
    event.preventDefault()

    this.say((await this.copy(this.textValue || new URL(this.urlValue, location.href).toString())) ? this.copiedValue : this.failedValue)
  }

  // A post sent on with a place in it: noted, by which way, so the platform
  // learns how invitations travel. The link itself goes on to the mail client
  // or WhatsApp; nothing waits on this.
  noted(event) {
    if (!this.noteValue) return
    const csrf = document.querySelector("meta[name='csrf-token']")?.content ?? ""
    const body = JSON.stringify({ name: "sent_with_place", way: event.params.way, post: this.postValue })
    try {
      fetch(this.noteValue, { method: "POST", keepalive: true, body,
                              headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf, Accept: "application/json" } })
    } catch { /* nothing lost worth keeping */ }
  }

  async copy(text) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // An older WebView, or a clipboard the page may not write to: the old way.
      const field = document.createElement("textarea")
      field.value = text
      field.setAttribute("readonly", "")
      field.style.position = "fixed"
      field.style.opacity = "0"
      document.body.appendChild(field)
      field.select()
      let copied = false
      try { copied = document.execCommand("copy") } catch { copied = false }
      field.remove()
      return copied
    }
  }

  say(text) {
    if (!this.hasLabelTarget) return
    const label = this.labelTarget
    const was = label.textContent
    label.textContent = text
    label.hidden = false
    clearTimeout(this.timer)
    this.timer = setTimeout(() => { label.hidden = true; label.textContent = was }, 1800)
  }

  disconnect() {
    clearTimeout(this.timer)
  }
}
